import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IntegrationsService } from '@app/modules/site-settings-integrations/services/integrations.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, finalize } from 'rxjs';

export interface IntegrationActionResult {
  success: boolean;
  title: string;
  message?: string;
}

const RUN_NOW_COOLDOWN_MS = 30000;

@Component({
  selector: 'app-integration-actions',
  templateUrl: './integration-actions.component.html',
  styleUrls: ['./integration-actions.component.scss']
})
export class IntegrationActionsComponent {
  @Input() integrationId: string;
  @Input() integrationType: string;
  @Output() result = new EventEmitter<IntegrationActionResult | null>();

  public isTesting: boolean = false;
  public isRunning: boolean = false;

  constructor(
    private integrationService: IntegrationsService,
    private translate: TranslateService,
  ) { }

  get canRunNow(): boolean {
    return this.integrationType === 'Adp' || this.integrationType === 'Payzaar';
  }

  testConnection() {
    this.isTesting = true;
    this.result.emit(null);

    this.testRequest()
      .pipe(finalize(() => this.isTesting = false))
      .subscribe(res => {
        this.result.emit({
          success: res.success === true,
          title: res.success === true
            ? this.translate.instant('Integrations-ConnectionSuccessful', { Default: 'Connection successful' })
            : this.translate.instant('Integrations-ConnectionFailed', { Default: 'Connection failed' }),
          message: res.message
        });
      });
  }

  runNow() {
    this.isRunning = true;
    this.result.emit(null);

    const request = this.integrationType === 'Adp'
      ? this.integrationService.runNowAdp(this.integrationId)
      : this.integrationService.runNowPayzaar(this.integrationId);

    request.subscribe({
      next: () => {
        this.result.emit({
          success: true,
          title: this.translate.instant('Integrations-RunStarted', { Default: 'Run started' }),
          message: this.translate.instant('Integrations-RunStartedMessage', { Default: 'The run has been queued and will begin shortly.' })
        });

        setTimeout(() => this.isRunning = false, RUN_NOW_COOLDOWN_MS);
      },
      error: () => {
        this.isRunning = false;
      }
    });
  }

  private testRequest(): Observable<{ success: boolean, message: string }> {
    switch (this.integrationType) {
      case 'Payworks':
        return this.integrationService.testPayworksConnection(this.integrationId);
      case 'iCIMS':
      case 'IcimsInbound':
        return this.integrationService.testIcimsConnection(this.integrationId);
      case 'Payzaar':
        return this.integrationService.testPayzaarConnection(this.integrationId);
      case 'Adp':
        return this.integrationService.testAdpConnection(this.integrationId);
      default:
        return EMPTY;
    }
  }
}
